import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { formatDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import { formatInr, truncateAmountParts } from "../../utils/formatAmount";

const formatLitersTruncated = (value) => {
  const parts = truncateAmountParts(value);
  if (!parts) return "0.00";
  return `${parts.sign}${parts.intPart}.${parts.fraction}`;
};

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const REQUEST_TYPES = ["trip_expense", "debit_note", "fuel_waive"];

const CHANGE_REQUEST_STATUS = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const normalizeRequestType = (raw) => {
  const t = String(raw || "").toLowerCase().replace(/-/g, "_");
  if (REQUEST_TYPES.includes(t)) return t;
  if (t === "trip") return "trip_expense";
  return "trip_expense";
};

const requestTypeNameToKind = (name) => {
  const t = String(name || "").toLowerCase();
  if (t.includes("debit")) return "debit_note";
  if (t.includes("fuel") || t.includes("waive")) return "fuel_waive";
  if (t.includes("expense") || t.includes("trip")) return "trip_expense";
  return "trip_expense";
};

const mapDebitNoteView = (data) => {
  const trip = data?.trip_context?.trip_details || {};
  const change = data?.debit_note_change || {};
  const tripId =
    trip.trip_ref_id != null && trip.trip_ref_id !== ""
      ? String(trip.trip_ref_id)
      : "—";

  return {
    tripId,
    amount: formatInr(change.amount),
    description: change.description ?? "—",
  };
};

const mapRequestMeta = (request) => {
  const statusNum =
    request?.status == null || request?.status === ""
      ? 0
      : Number(request.status);
  return {
    submittedAt: formatDateTime(request?.submitted_at),
    status: CHANGE_REQUEST_STATUS[statusNum] ?? "Pending",
    requestRefId: request?.request_ref_id ?? "—",
    requestReason: request?.request_reason ?? "",
    rejectReason: request?.reject_reason ?? "",
  };
};

const receiptLabelFromUrl = (url) => {
  if (!url) return "Receipt";
  try {
    const name = new URL(url).pathname.split("/").pop();
    return name || "Receipt";
  } catch {
    const segment = String(url).split("/").pop() || "";
    return segment.split("?")[0] || "Receipt";
  }
};

const mapExpenseLineRow = (line, which) => {
  const block = which === "current" ? line?.current : line?.requested;
  return {
    id: line?.trip_expense_id ?? line?.s_no,
    amount: Number(block?.amount) || 0,
    dateTime: formatDateTime(line?.line_created_at),
    receiptName: receiptLabelFromUrl(block?.bill_image_url),
    receiptUrl: block?.bill_image_url ?? "",
    description: block?.description ?? "",
  };
};

const formatAwardDisplay = (n) => formatInr(n);

const resolveWaiveDriverName = (change, driverDetails) => {
  const primary = driverDetails?.primary_driver_details || {};
  const secondary = driverDetails?.secondary_driver_details || {};
  const primaryId = change?.primary_driver_id;
  const secondaryId = change?.secondary_driver_id;

  if (primaryId && primary.driver_id === primaryId) {
    return primary.driver_name ?? "—";
  }
  if (secondaryId && secondary.driver_id === secondaryId) {
    return secondary.driver_name ?? "—";
  }
  return primary.driver_name || secondary.driver_name || "—";
};

const mapFuelWaiveView = (data) => {
  const trip = data?.trip_context?.trip_details || {};
  const change = data?.waive_off_change || {};
  const driverDetails = data?.trip_context?.driver_details || {};

  const tripId =
    trip.trip_ref_id != null && trip.trip_ref_id !== ""
      ? String(trip.trip_ref_id)
      : "—";

  const balanceBefore = Number(change.award_balance_amount) || 0;
  const waiveAmount = Number(change.award_amount) || 0;
  const waiveLiters = change.waive_fuel;
  const balanceAfter = balanceBefore - waiveAmount;

  return {
    tripId,
    driverName: resolveWaiveDriverName(change, driverDetails),
    currentAwardBalance: formatAwardDisplay(balanceBefore),
    waiveAmount: formatAwardDisplay(waiveAmount),
    waiveSubtitle:
      waiveLiters != null && waiveLiters !== ""
        ? `Waive Of Fuel (${formatLitersTruncated(waiveLiters)} liter)`
        : "Waive Of Fuel",
    balanceAwardAmount: formatAwardDisplay(balanceAfter),
  };
};

const mapTripDetailsView = (data) => {
  const ctx = data?.trip_context || {};
  const trip = ctx.trip_details || {};
  const vehicle = trip.vehicle_id || {};
  const company = trip.company_id || {};
  const companyLoc = company.company_location_id || {};
  const miplLoc = trip.mipl_location_id || {};

  return {
    company: company.name ?? "—",
    tripId:
      trip.trip_ref_id != null && trip.trip_ref_id !== ""
        ? String(trip.trip_ref_id)
        : "—",
    companyLocation: companyLoc.name ?? "—",
    startDateTime: formatDateTime(trip.start_date_time ?? trip.date_time),
    miplLocation: miplLoc.name ?? "—",
    idealAverage:
      vehicle.ideal_average != null && vehicle.ideal_average !== ""
        ? `${vehicle.ideal_average} average`
        : "—",
    challanNumber: trip.challan_number ?? "—",
    tripAmount: formatInr(trip.trip_amount ?? ctx.trip_amount),
    vehicle: vehicle.name ?? "—",
  };
};

function statusClassName(status) {
  switch (status) {
    case "Approved":
      return "text-green-600";
    case "Rejected":
      return "text-danger";
    default:
      return "text-yellow-600";
  }
}

function MetaBar({
  className = "",
  submittedAt = "—",
  status = "Pending",
  rejectReason = "",
}) {
  return (
    <div
      className={`flex flex-col gap-2 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-end sm:gap-4 ${className}`}
    >
      <span>
        Submitted Date &amp; Time:{" "}
        <b className="text-gray-900">{submittedAt}</b>
      </span>
      <span
        className="hidden h-6 w-px shrink-0 bg-gray-300 sm:block"
        aria-hidden
      />
      <span className="flex flex-col items-start sm:items-end">
        <span>
          Status: <b className={statusClassName(status)}>{status}</b>
        </span>
        {status === "Rejected" && rejectReason ? (
          <span className="mt-1 max-w-xs text-left text-sm text-red-500 whitespace-pre-wrap break-words sm:text-right">
            {rejectReason}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function StatCard({ value, label }) {
  return (
    <div className="rounded-lg bg-gray-100 px-4 py-4 text-center sm:text-left">
      <p className="text-lg font-bold leading-tight text-gray-900 sm:text-xl">
        {value}
      </p>
      <p className="mt-2 text-xs font-medium text-gray-500 sm:text-sm">{label}</p>
    </div>
  );
}

function TripField({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-200/80 py-2.5 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function ReceiptImageModal({ receipt, onClose }) {
  if (!receipt?.url) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="receipt-modal-title"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white px-6 py-8 shadow-xl sm:max-w-lg sm:px-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="receipt-modal-title"
          className="text-center text-lg font-bold text-gray-900"
        >
          Receipt Image
        </h3>

        <div className="mt-6 flex justify-center">
          <img
            src={receipt.url}
            alt={receipt.name || "Receipt"}
            className="mx-auto block h-auto max-h-[60vh] w-full max-w-full object-contain"
          />
        </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="min-w-[140px] rounded-full border border-danger bg-danger px-12 py-2.5 text-base font-semibold text-white transition hover:opacity-90"
          >
            Okay
          </button>
        </div>
      </div>
    </div>
  );
}

function ExpenseTable({ rows, onViewDescription, onViewReceipt }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
      <div className="table-responsive">
        <table className="w-full text-sm">
          <thead className="bg-[#030253] text-white">
            <tr>
              <th className="p-3 text-left font-medium">S.No.</th>
              <th className="p-3 text-left font-medium">Amount Spent</th>
              <th className="p-3 text-left font-medium">Date &amp; Time</th>
              <th className="p-3 text-left font-medium">Receipt Image</th>
              <th className="p-3 text-left font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.id}
                className={`border-b border-gray-100 ${
                  idx % 2 === 0 ? "bg-white" : "bg-[#efeff23b]"
                }`}
              >
                <td className="p-3 text-gray-800">
                  {String(idx + 1).padStart(2, "0")}
                </td>
                <td className="p-3 font-medium text-secondary">
                  {formatInr(row.amount)}
                </td>
                <td className="p-3 font-medium text-secondary">{row.dateTime}</td>
                <td className="p-3">
                  {row.receiptUrl ? (
                    <button
                      type="button"
                      onClick={() =>
                        onViewReceipt({
                          url: row.receiptUrl,
                          name: row.receiptName,
                        })
                      }
                      className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-medium text-secondary transition hover:border-primary hover:underline"
                    >
                      <span aria-hidden>📎</span>
                      {row.receiptName}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs text-gray-700">
                      <span aria-hidden>📎</span>
                      {row.receiptName}
                    </span>
                  )}
                </td>
                <td className="p-3">
                  <button
                    type="button"
                    onClick={() => onViewDescription(row.description)}
                    className="font-semibold text-secondary hover:underline"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ViewRequestDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id") || "";
  const backToList = useBackToList("/my-requests");
  const typeFromUrl = useMemo(
    () => normalizeRequestType(searchParams.get("type")),
    [searchParams]
  );

  const [loading, setLoading] = useState(Boolean(requestId));
  const [meta, setMeta] = useState({
    submittedAt: "—",
    status: "Pending",
    requestRefId: "—",
    requestReason: "",
    rejectReason: "",
  });
  const [requestKind, setRequestKind] = useState(typeFromUrl);
  const [debitDetails, setDebitDetails] = useState(null);
  const [fuelWaiveDetails, setFuelWaiveDetails] = useState(null);
  const [tripDetails, setTripDetails] = useState(null);
  const [previousExpenseRows, setPreviousExpenseRows] = useState([]);
  const [updatedExpenseRows, setUpdatedExpenseRows] = useState([]);
  const [descModal, setDescModal] = useState(null);
  const [receiptModal, setReceiptModal] = useState(null);

  useEffect(() => {
    setSubheading("View Request Details");
    return () => setSubheading("");
  }, [setSubheading]);

  useEffect(() => {
    if (!requestId) {
      setLoading(false);
      setRequestKind(typeFromUrl);
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setLoading(false);
        return;
      }

      setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.viewChangeRequest}?request_id=${encodeURIComponent(
        requestId
      )}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          return;
        }

        const data = payload?.data || {};
        const request = data.request || {};

        setMeta(mapRequestMeta(request));
        setRequestKind(
          requestTypeNameToKind(request.request_type) || typeFromUrl
        );

        if (data.debit_note_change) {
          setDebitDetails(mapDebitNoteView(data));
        } else {
          setDebitDetails(null);
        }

        if (data.waive_off_change) {
          setFuelWaiveDetails(mapFuelWaiveView(data));
        } else {
          setFuelWaiveDetails(null);
        }

        const expenseLines = data.expense_change?.lines;
        if (Array.isArray(expenseLines) && expenseLines.length > 0) {
          setTripDetails(mapTripDetailsView(data));
          setPreviousExpenseRows(
            expenseLines.map((line) => mapExpenseLineRow(line, "current"))
          );
          setUpdatedExpenseRows(
            expenseLines.map((line) => mapExpenseLineRow(line, "requested"))
          );
        } else {
          setTripDetails(null);
          setPreviousExpenseRows([]);
          setUpdatedExpenseRows([]);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load request details."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [requestId, typeFromUrl]);

  const showMissingId = !requestId;
  const hasExpenseData =
    tripDetails && (previousExpenseRows.length > 0 || updatedExpenseRows.length > 0);

  return (
    <div className="min-h-screen bg-white p-6 font-sf-pro">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Link to={backToList} className="btn btn-primary">
          Back
        </Link>
      </div>

      {requestKind === "trip_expense" && (
        <>
          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Previous Details</h2>
              {meta.requestRefId && meta.requestRefId !== "—" && (
                <p className="mt-1 text-sm text-gray-500">
                  Request ID:{" "}
                  <span className="font-medium text-gray-800">
                    {meta.requestRefId}
                  </span>
                </p>
              )}
            </div>
            <MetaBar
              submittedAt={meta.submittedAt}
              status={meta.status}
              rejectReason={meta.rejectReason}
            />
          </div>

          {showMissingId ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Missing request ID. Open this page from My Requests.
            </p>
          ) : loading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Loading expense request details…
            </p>
          ) : !hasExpenseData ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No expense change details found for this request.
            </p>
          ) : (
            <>
              {meta.requestReason ? (
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-medium text-gray-500">
                    Reason For Change
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-gray-900">
                    {meta.requestReason}
                  </p>
                </div>
              ) : null}

              <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-base font-bold text-[#030253]">
                  Trip Details
                </h3>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <TripField label="Company" value={tripDetails.company} />
                    <TripField label="Trip ID" value={tripDetails.tripId} />
                    <TripField
                      label="Company Location"
                      value={tripDetails.companyLocation}
                    />
                    <TripField
                      label="Start Date & Time"
                      value={tripDetails.startDateTime}
                    />
                    <TripField
                      label="MIPL Location"
                      value={tripDetails.miplLocation}
                    />
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <TripField
                      label="Ideal Average"
                      value={tripDetails.idealAverage}
                    />
                    <TripField
                      label="Delivery Challan Number"
                      value={tripDetails.challanNumber}
                    />
                    <TripField
                      label="Trip Amount"
                      value={tripDetails.tripAmount}
                    />
                    <TripField label="Vehicle" value={tripDetails.vehicle} />
                  </div>
                </div>

                <h3 className="mb-3 mt-8 text-base font-bold text-gray-900">
                  Expense Details
                </h3>
                <ExpenseTable
                  rows={previousExpenseRows}
                  onViewDescription={setDescModal}
                  onViewReceipt={setReceiptModal}
                />
              </div>

              <h2 className="mb-3 mt-10 text-lg font-bold text-gray-900">
                Updated Expense Details
              </h2>
              <p className="mb-3 text-sm text-gray-500">
                Requested changes for this expense change request.
              </p>
              <ExpenseTable
                rows={updatedExpenseRows}
                onViewDescription={setDescModal}
                onViewReceipt={setReceiptModal}
              />
            </>
          )}
        </>
      )}

      {requestKind === "debit_note" && (
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm md:p-6">
          <div className="mb-6 flex flex-col gap-4 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-bold text-gray-900">Details</h2>
            <MetaBar
              className="sm:justify-end"
              submittedAt={meta.submittedAt}
              status={meta.status}
              rejectReason={meta.rejectReason}
            />
          </div>

          <h3 className="mb-4 text-base font-bold text-[#030253]">Debit Note Details</h3>

          {showMissingId ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Missing request ID. Open this page from My Requests.
            </p>
          ) : loading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Loading debit note details…
            </p>
          ) : !debitDetails ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No debit note details found for this request.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <StatCard value={debitDetails.tripId} label="Trip ID" />
                <StatCard value={debitDetails.amount} label="Amount" />
              </div>

              <div className="mt-6 rounded-lg bg-gray-100 p-4 md:p-5">
                <p className="mb-2 text-sm font-medium text-gray-500">Description</p>
                <p className="text-sm leading-relaxed text-gray-900">
                  {debitDetails.description}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {requestKind === "fuel_waive" && (
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm md:p-6">
          <div className="mb-6 flex flex-col gap-4 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Details</h2>
              {meta.requestRefId && meta.requestRefId !== "—" && (
                <p className="mt-1 text-sm text-gray-500">
                  Request ID:{" "}
                  <span className="font-medium text-gray-800">
                    {meta.requestRefId}
                  </span>
                </p>
              )}
            </div>
            <MetaBar
              className="sm:justify-end"
              submittedAt={meta.submittedAt}
              status={meta.status}
              rejectReason={meta.rejectReason}
            />
          </div>

          <h3 className="mb-4 text-base font-bold text-[#030253]">
            Fuel Waive Off Details
          </h3>

          {showMissingId ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Missing request ID. Open this page from My Requests.
            </p>
          ) : loading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Loading fuel waive-off details…
            </p>
          ) : !fuelWaiveDetails ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No fuel waive-off details found for this request.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <StatCard value={fuelWaiveDetails.tripId} label="Trip ID" />
                <StatCard
                  value={fuelWaiveDetails.driverName}
                  label="Driver Name"
                />
                <StatCard
                  value={fuelWaiveDetails.currentAwardBalance}
                  label="Current Award Balance"
                />
                <div className="rounded-lg bg-gray-100 px-4 py-4 text-center sm:text-left">
                  <p className="text-lg font-bold leading-tight text-gray-900 sm:text-xl">
                    {fuelWaiveDetails.waiveAmount}
                  </p>
                  <p className="mt-2 text-xs font-medium text-gray-500 sm:text-sm">
                    {fuelWaiveDetails.waiveSubtitle}
                  </p>
                </div>
                <StatCard
                  value={fuelWaiveDetails.balanceAwardAmount}
                  label="Balance Award Amount"
                />
              </div>

              <div className="mt-6 max-w-4xl rounded-lg bg-gray-100 p-4 md:p-5">
                <p className="mb-2 text-sm font-medium text-gray-500">
                  Reason For Waive-Off
                </p>
                <p className="text-sm leading-relaxed text-gray-900">
                  {meta.requestReason || "—"}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {descModal !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-[80vh] w-full max-w-md overflow-auto rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-[#030253]">Description</h3>
            <p className="mt-3 text-sm text-gray-700">{descModal}</p>
            <button
              type="button"
              onClick={() => setDescModal(null)}
              className="mt-6 w-full rounded-lg bg-[#030253] py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ReceiptImageModal
        receipt={receiptModal}
        onClose={() => setReceiptModal(null)}
      />

      <Toastify />
    </div>
  );
};

export default ViewRequestDetails;
